import { Fragment, useMemo } from "react"
import { useField, useFormikContext } from "formik"
import { Divider } from "@mui/material"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { DynamicConfigKey, MeetingServiceTabValues } from "../../form/formTypes"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { MeetingServicesPlaceholder } from "./MeetingServicesPlaceholder"
import { MeetingServicePriceListState, ProductWithQuantity, SelectedPriceListItem } from "../../../createOrderTypes"
import { MeetingServiceProduct } from "./MeetingServiceProduct"
import { ProductUpdateQuantityProps } from "./MeetingServices"
import { queryClient } from "../../../../../../core/api/queryClient"
import {
    FetchProductsResponse,
    MerchantProductsQuery,
} from "../../../../../../modules/merchant/admin/products/productsApi"
import { OrderSummaryItemBox } from "./OrderSummaryItemBox"
import { filterShops } from "../../shop/helpers"

type Props = {
    serialNumber: number
    deliveryTime: string
    orderLines: ProductWithQuantity[]
    deliveryDate: string | null
    isRefundOrder: boolean
    onUpdateQuantity: (props: ProductUpdateQuantityProps) => void
    isEditable: boolean
}

export const MeetingServiceDelivery = ({
    serialNumber,
    deliveryTime,
    orderLines,
    deliveryDate,
    isRefundOrder,
    onUpdateQuantity,
    isEditable,
}: Props) => {
    const { submitCount } = useFormikContext<MeetingServiceTabValues>()
    const [{ value: priceListState }] = useField<MeetingServicePriceListState>("priceList")
    const priceList: SelectedPriceListItem | null = priceListState.item
    const [, deliveriesMeta] = useField(DynamicConfigKey.MULTIPLE_DELIVERIES)

    if (!priceList) return null

    const products: FetchProductsResponse | undefined = queryClient.getQueryData([
        MerchantProductsQuery.LIST,
        { merchantId: priceList.merchantId, priceListId: priceList.priceListId, isActive: true },
    ])

    const filteredShops = useMemo(() => {
        const list = filterShops(false, undefined, products?.productGroups)
        const ids = new Set<number>()
        for (const group of list || []) {
            for (const p of group.products || []) {
                if (typeof p.id === "number") ids.add(p.id)
            }
        }
        return ids
    }, [products])

    const dateTimeFactory = useDateTime()
    const formattedDate = deliveryDate && dateTimeFactory.fromISO(deliveryDate).toFormat("d MMMM")
    const time = dateTimeFactory.fromHourMinute(deliveryTime).toFormat("t")

    const hasDeliveriesError = submitCount > 0 && typeof deliveriesMeta.error === "string"

    return (
        <OrderSummaryItemBox
            title={
                <LocalizedStrict
                    id="create-order-order-summary-meeting-services-delivery-time"
                    vars={{ number: serialNumber }}
                >
                    <>Delivery time:</>
                </LocalizedStrict>
            }
            titleDetails={`${formattedDate}, ${time}`}
        >
            {orderLines.length > 0 ? (
                orderLines.map((p, index) => (
                    <Fragment key={`${deliveryTime}-${p.id}`}>
                        {!!index && <Divider flexItem />}
                        <MeetingServiceProduct
                            product={p}
                            deliveryTime={deliveryTime}
                            isRefundOrder={isRefundOrder}
                            onUpdateQuantity={onUpdateQuantity}
                            isEditable={isEditable}
                            availableProductIds={filteredShops}
                        />
                    </Fragment>
                ))
            ) : (
                <MeetingServicesPlaceholder isError={hasDeliveriesError} />
            )}
        </OrderSummaryItemBox>
    )
}
